import { FC } from 'react';
import styles from './CheckerboardCell.module.scss';
import CheckerboardBanner from '../CheckerboardBanner';
import classNames from 'classnames';
import {
  BookingType,
  UpdateClosureObjectType,
} from '../../redux/types/bookingTypes';
import moment from 'moment';
import PriceBanner from '../CheckerboardPrice/PriceBanner';
import { PriceVariant } from '../../redux/types/pricesTypes';
import ClosureBanner from '../CheckerboardBanner/ClosureBanner';
import { WarningIcon } from '../../assets/icons';
import {
  getTransitionIndicatorGeometry,
  getTransitionLabel,
  BookingTransition,
} from '../../utils/functions/bookingTransitions';

type CheckerboardCellProps = {
  itemWidth: number;
  id: number | string;
  bookings?: BookingType[];
  longBookings?: BookingType[];
  transitions?: BookingTransition[];
  onTransitionClick?: (transition: BookingTransition) => void;
  index: number;
  day: moment.Moment;
  isToday: boolean;
  isSelected: boolean;
  isWeekend: boolean;
  price?: {
    summ: number;
    variant: PriceVariant;
    certId: number | undefined;
  } | null;
  isPrice?: boolean;
  closure?: UpdateClosureObjectType | undefined;
  longClosure?: UpdateClosureObjectType | undefined | null;
};

const CheckerboardCell: FC<CheckerboardCellProps> = ({
  itemWidth,
  id,
  bookings = [],
  longBookings = [],
  transitions = [],
  onTransitionClick,
  index,
  day,
  isToday,
  isSelected,
  isWeekend,
  price,
  isPrice,
  closure,
  longClosure,
}) => {
  const isLast = day.startOf('day') < moment().startOf('day') ? true : false;

  return (
    <div
      className={classNames(styles.tableGrid, {
        [styles.weekendGrid]: isWeekend,
        [styles.todayGrid]: isToday,
        [styles.selectedGrid]: isSelected,
      })}
      id={`id${id}${day.unix()}`}
      style={{
        width: itemWidth,
      }}
    >
      {!isPrice &&
        bookings.map((booking) => (
          <CheckerboardBanner
            booking={booking}
            cellWidth={itemWidth}
            key={`start-${booking.id}`}
            currentDay={day}
            type="start"
          />
        ))}
      {!isPrice &&
        longBookings.map((booking) => (
          <CheckerboardBanner
            booking={booking}
            cellWidth={itemWidth}
            key={`ending-${booking.id}`}
            currentDay={day}
            type="ending"
          />
        ))}

      {!isPrice &&
        transitions.map((transition) => {
          const indicator = getTransitionIndicatorGeometry(
            transition,
            itemWidth,
          );

          const label = getTransitionLabel(transition);
          const key = `${transition.previousBookingId}-${transition.nextBookingId}`;

          return (
            <button
              className={classNames(
                styles.transitionIndicator,
                styles[transition.kind],
              )}
              style={indicator}
              key={key}
              type="button"
              aria-label={label}
              onClick={(event) => {
                event.stopPropagation();
                onTransitionClick?.(transition);
              }}
            >
              <span className={styles.mobileWarning} aria-hidden="true">
                <WarningIcon width="9" height="18" fill="#b91212" />
              </span>
              <span className={styles.transitionTooltip} role="tooltip">
                {label}
              </span>
            </button>
          );
        })}

      {closure && !isPrice && (
        <ClosureBanner
          closure={closure}
          cellWidth={itemWidth}
          key={index}
          currentDay={day}
          type="start"
        />
      )}
      {longClosure && !isPrice && (
        <ClosureBanner
          closure={longClosure}
          cellWidth={itemWidth}
          key={index}
          currentDay={day}
          type="ending"
        />
      )}
      {!isPrice &&
        price &&
        !closure &&
        !longClosure &&
        bookings.length === 0 &&
        longBookings.length === 0 && (
          <div className={styles.priceBanner}>{Math.floor(price?.summ)}</div>
        )}

      {isPrice && price && (
        <PriceBanner cellWidth={itemWidth} price={price} isLast={isLast} />
      )}
    </div>
  );
};

export default CheckerboardCell;
